import { ChevronRight } from "lucide-react";

import { cn } from "@/lib/utils";

export interface EntityChainLink {
  /** Stable react key, and the text shown when `name` is missing — pass the
   *  raw id, never "". */
  id: string;
  /** What KIND of thing this link is, in the reader's words: "Company",
   *  "Location", "Account". Rendered as the small label above the name. */
  label: string;
  /** Readable name. Null/empty falls back to `id`. */
  name?: string | null;
  /** The link the row is actually about — the end of the chain. */
  current?: boolean;
}

/**
 * Breadcrumb trail of an entity's ancestry, each link tagged with what KIND of
 * thing it is.
 *
 * Built for the question "where do I actually look?". A row identified only by
 * its deepest name is ambiguous — "JIFFY LUBE # 1307" means nothing until you
 * know which company owns it and which integration carries it. Showing the
 * whole chain, with each link's grain named, turns an alert into an address.
 *
 * The last link is the subject and reads solid; ancestors are muted context.
 * Pass `onLinkClick` to make ancestors navigable — the current link never
 * invokes it, since you are already there.
 *
 * Reach for this over a hand-rolled `a · b · c` string whenever the ancestry
 * is something the reader has to act on.
 */
export function EntityChain({
  links,
  onLinkClick,
  className,
}: {
  links: EntityChainLink[];
  /** Makes ancestor links clickable. Omit for a static trail. */
  onLinkClick?: (link: EntityChainLink, index: number) => void;
  className?: string;
}) {
  if (!links.length) return null;
  return (
    <nav
      aria-label="Entity chain"
      className={cn("flex flex-wrap items-center gap-x-1 gap-y-2", className)}
    >
      {links.map((link, i) => {
        const text = link.name?.trim() || link.id;
        const isCurrent = link.current ?? i === links.length - 1;
        const clickable = !isCurrent && onLinkClick != null;
        return (
          <div key={`${link.id}-${i}`} className="flex items-center gap-1">
            {i > 0 && (
              <ChevronRight
                aria-hidden
                className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60"
              />
            )}
            <div className="flex flex-col leading-tight">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {link.label}
              </span>
              {clickable ? (
                <button
                  type="button"
                  onClick={() => onLinkClick(link, i)}
                  className="max-w-56 truncate text-left text-sm text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                  title={text}
                >
                  {text}
                </button>
              ) : (
                <span
                  className={cn(
                    "max-w-56 truncate text-sm",
                    isCurrent ? "font-semibold text-foreground" : "text-muted-foreground",
                  )}
                  title={text}
                  aria-current={isCurrent ? "true" : undefined}
                >
                  {text}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </nav>
  );
}
